/**
 * BlobオブジェクトをBase64エンコードされた文字列に変換します
 *
 * @param {Blob} blob - 変換するBlobオブジェクト
 * @returns {Promise<string>} Base64エンコードされた文字列を返すPromise
 * @throws {Error} 変換が失敗した場合はエラーを投げます
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Base64 conversion failed'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * 指定されたURLから画像を取得し、Base64エンコード文字列に変換します。
 *
 * この関数は、クロスオリジンリソースとの互換性を向上させるため、まず `cors` モードで
 * 画像の取得を試みます。リクエストが失敗した場合は、代替手段として `no-cors` モードを
 * 使用してフォールバックします。取得した画像は `FileReader` を使用してBase64エンコード
 * 文字列に変換されます。
 *
 * @param {string} imageUrl - 取得して変換する画像のURL
 * @returns {Promise<string>} 画像のBase64文字列表現に解決されるPromise
 * @throws {Error} 初回取得とフォールバックアプローチの両方が失敗した場合、
 *                 またはBase64変換が失敗した場合にエラーを投げます
 */
export const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
  try {
    // イメージのフェッチ
    const response = await fetch(imageUrl, {
      mode: 'cors',
      cache: 'no-cache'
    });

    const blob = await response.blob();

    // ヘルパー関数を使用してBlobをBase64に変換
    return await blobToBase64(blob);
  } catch (error) {
    console.error('Error converting image to Base64:', error);
    try {
      const fallbackResponse = await fetch(imageUrl, {
        mode: 'no-cors',
        cache: 'no-cache'
      });

      const blob = await fallbackResponse.blob();

      // 同じヘルパー関数を再利用
      return await blobToBase64(blob);
    } catch (fallbackError) {
      console.error('Fallback approach also failed:', fallbackError);
      throw fallbackError;
    }
  }
};
